import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, KeyRound, Lock, Unlock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PageTitle, AdminButton } from "@/components/admin/kit";

export const KEY_FEATURES = [
  "Pinned Showhome hotspots",
  "A smooth gliding cinematic opening",
  "Room pictures in the menus",
  "Floor plans brought in automatically",
  "No Giraffe360 player controls on the 360° view",
];

export function MissingList({ dark = false }) {
  return (
    <div data-testid="g360-key-features">
      <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${dark ? "text-white/50" : "text-slate-500"}`}>What the key unlocks</p>
      <ul className={`space-y-1.5 text-sm ${dark ? "text-white/70" : "text-slate-600"}`}>
        {KEY_FEATURES.map((f) => <li key={f} className="flex gap-2"><Unlock className={`w-3.5 h-3.5 mt-1 shrink-0 ${dark ? "text-[#C9A227]" : "text-amber-600"}`} />{f}</li>)}
      </ul>
      <p className={`flex items-center gap-2 text-xs font-semibold mt-3 ${dark ? "text-emerald-300" : "text-emerald-700"}`} data-testid="g360-key-secure"><Lock className="w-3.5 h-3.5" />Key is secured and encrypted</p>
    </div>
  );
}

export function useG360Key() {
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/tdv/g360-key").then((r) => setK(r.data)).catch(() => setK({ connected: false })); }, []);
  return [k, setK];
}

export default function G360KeyPage() {
  const [k, setK] = useG360Key();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  if (!k) return null;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/tdv/g360-key", { key }); setK(data); setKey(""); toast.success("Key checked and saved. Open a tour and pick its Giraffe360 project."); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Remove your Giraffe360 API key? Your tours go back to Giraffe360's own player and lose the features below.")) return;
    const { data } = await api.delete("/tdv/g360-key"); setK(data); toast.success("Key removed");
  };
  return (
    <div data-testid="g360-key-page">
      <PageTitle title="Giraffe360 API" sub="Connect your Giraffe360 account so we can show your rooms in our own 360° viewer." />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl items-start">
        <div className="rounded-3xl bg-white border p-6 space-y-4" data-testid="g360-key-card">
          <div className="flex items-center gap-3"><KeyRound className="w-5 h-5 text-slate-500" /><p className="font-semibold flex-1">Your Giraffe360 API key</p></div>
          {k.connected ? (
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 text-emerald-800 px-3 py-2 text-sm" data-testid="g360-key-saved"><Check className="w-4 h-4" /><span className="flex-1">Key ending {k.last4} is connected</span><button onClick={remove} className="text-rose-600 text-xs font-semibold" data-testid="g360-key-remove">Remove</button></div>
          ) : (
            <>
              <p className="text-xs text-slate-500">Giraffe360 issues API keys through your customer success contact there. We check the key works, store it encrypted, only show its last 4 characters and only use it to read your own projects.</p>
              <div className="flex gap-2"><Input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Paste your Giraffe360 API key" data-testid="g360-key-input" />
                <AdminButton onClick={save} disabled={busy || key.trim().length < 10} data-testid="g360-key-save">{busy ? "Checking…" : "Save"}</AdminButton></div>
            </>
          )}
          {k.connected && <Link to="/giraffe/app" className="inline-flex text-sm font-semibold text-bp" data-testid="g360-key-to-tours">Go to your tours →</Link>}
        </div>
        <div className={`rounded-3xl border p-6 space-y-3 ${k.connected ? "bg-emerald-50/50" : "bg-amber-50/60"}`} data-testid="g360-key-explain">
          <p className="font-semibold flex items-center gap-2">{k.connected ? <><Unlock className="w-4 h-4 text-emerald-600" />Unlocked with your key</> : <><Lock className="w-4 h-4 text-amber-600" />Missing without a key</>}</p>
          <MissingList />
          <p className="text-xs text-slate-500 pt-2">Without a key your tours still work: we show Giraffe360's own player with your branding, menus, room jumps, info windows, AI assistant, viewing requests, password and stats around it.</p>
        </div>
      </div>
    </div>
  );
}

export function G360TourCard({ t, setT }) {
  const [k] = useG360Key();
  const [list, setList] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (k?.connected && !t.g360_project_id) api.get("/tdv/g360-projects").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [k?.connected, t.g360_project_id]);
  if (!k) return null;
  const linkIt = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/tdv/tours/${t.id}/g360-link`, { project_id: pick }); setT(data); toast.success("Your rooms are in. Name them, add hotspots, then Save."); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const unlink = async () => {
    if (!window.confirm("Switch back to Giraffe360's own player? Your pinned hotspots stay saved but won't show.")) return;
    const { data } = await api.delete(`/admin/tdv/tours/${t.id}/g360-link`); setT(data);
  };
  return (
    <div className="rounded-3xl bg-white border p-5 space-y-3" data-testid="g360-tour-card">
      <p className="font-semibold">Viewer</p>
      {t.g360_project_id ? (
        <>
          <p className="text-sm text-emerald-800 bg-emerald-50 rounded-xl p-3" data-testid="g360-tour-linked">Using your Giraffe360 panoramas from <b>{t.g360_project_name || "your project"}</b> in our own viewer. Pinned hotspots and the smooth cinematic opening are on.</p>
          <button onClick={unlink} className="text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="g360-tour-unlink">Switch back to Giraffe360's player</button>
        </>
      ) : !k.connected ? (
        <>
          <p className="text-sm text-slate-600">This tour uses Giraffe360's own player. <b>Without your Giraffe360 API key, these are missing:</b></p>
          <MissingList />
          <Link to="/giraffe/app/connect" className="inline-flex h-9 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold items-center" data-testid="g360-tour-add-key">Add your API key</Link>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">Pick the Giraffe360 project for this tour to switch on pinned hotspots, the smooth cinematic opening and room pictures.</p>
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="h-10 w-full rounded-xl border px-3 text-sm bg-white" data-testid="g360-tour-project">
            <option value="">{list ? (list.length ? "Choose a project…" : "No projects found on your account") : "Loading your projects…"}</option>
            {(list || []).map((p) => <option key={p.id} value={p.id}>{p.name || p.id} ({p.rooms} rooms){p.tour_url && p.tour_url === t.giraffe_url ? " · this tour" : ""}</option>)}
          </select>
          <AdminButton onClick={linkIt} disabled={!pick || busy} data-testid="g360-tour-link">{busy ? "Bringing in your rooms…" : "Use my Giraffe360 panoramas"}</AdminButton>
        </>
      )}
    </div>
  );
}
